import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-13296">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-13297">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-13298">
              <span className="text-xs font-bold text-indigo-800" data-id="element-13299">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-13300">
            <div className="text-center" data-id="element-13301">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13302">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-13303">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-13304">→</div>
            <div className="text-center" data-id="element-13305">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13306">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-13307">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-13308">
            <table className="w-full text-sm" data-id="element-13309">
              <thead data-id="element-13310">
                <tr className="bg-indigo-100/70" data-id="element-13311">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13312">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13313">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-13314">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-13315">
                      <td className="px-3 py-1.5" data-id="element-13316">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-13317">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-13318">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13319">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13320">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-13321">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13322">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-13323">
            <span className="text-2xl" data-id="element-13324">📝</span>
            <div className="text-left" data-id="element-13325">
              <div className="text-sm font-bold text-indigo-900" data-id="element-13326">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-13327">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-13328">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-13329">
            <div className="text-center" data-id="element-13330">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13331">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-13332">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-13333">→</div>
            <div className="text-center" data-id="element-13334">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13335">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-13336">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-13337">
          <table className="w-full" data-id="element-13338">
            <thead data-id="element-13339">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-13340">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13341">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13342">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-13343">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-13344">
                    <td className="px-4 py-3" data-id="element-13345">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-13346">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-13347">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13348">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-13349">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-13350">
            <div className="flex items-start gap-3" data-id="element-13351">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-13352">
                <span className="text-blue-600 text-lg" data-id="element-13353">🔍</span>
              </div>
              <div data-id="element-13354">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-13355">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13356">
                  De <strong data-id="element-13357">stam</strong> krijg je door <strong data-id="element-13358">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-13359">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-13360">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13361">
              <div className="flex items-start gap-3" data-id="element-13362">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13363">
                  <span className="text-emerald-600 text-lg" data-id="element-13364">📋</span>
                </div>
                <div data-id="element-13365">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13366">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13367">
                    <p data-id="element-13368">
                      <strong data-id="element-13369">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13370">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-13371">
                      <strong data-id="element-13372">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13373">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13374">
                      <strong data-id="element-13375">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13376">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13377">
                      <strong data-id="element-13378">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13379">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13380">
              <div className="flex items-start gap-3" data-id="element-13381">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13382">
                  <span className="text-emerald-600 text-lg" data-id="element-13383">📋</span>
                </div>
                <div data-id="element-13384">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13385">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13386">
                    <p data-id="element-13387">
                      <strong data-id="element-13388">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-13389">
                      <strong data-id="element-13390">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13391">
                      <em data-id="element-13392">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13393">
              <div className="flex items-start gap-3" data-id="element-13394">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13395">
                  <span className="text-emerald-600 text-lg" data-id="element-13396">📋</span>
                </div>
                <div data-id="element-13397">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13398">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13399">
                    <p data-id="element-13400">
                      <strong data-id="element-13401">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-13402">
                      <strong data-id="element-13403">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13404">
                      <em data-id="element-13405">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13406">
            <div className="flex items-start gap-3" data-id="element-13407">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13408">
                <span className="text-amber-600 text-lg" data-id="element-13409">💡</span>
              </div>
              <div data-id="element-13410">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13411">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13412">
                  Bij <strong data-id="element-13413">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-13414">jij loopt</em> wordt{' '}
                  <em data-id="element-13415">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}